// src/pages/StaffConversationPage.jsx
//
// Shared by both doctor and navigator routes — both portals already use the
// same AppShell (components/Layout.js), confirmed by reading both
// DoctorPatientDetailPage.jsx and PatientDetailPage.js directly, so one
// page serves both rather than maintaining two near-identical copies.
//
// Routes (see App.js):
//   /doctor/patients/:patientId/chat  → conversationType="PATIENT_DOCTOR"
//   /patients/:patientId/chat         → conversationType="PATIENT_NAVIGATOR"
//     (navigator portal's existing unprefixed /patients/:id convention)
//
// On mount, create-or-gets the conversation with that patient (the backend
// resolves "my own id" — doctor/navigator — from the JWT; this page only
// ever sends the patient's id). If no eligible relationship exists yet
// (PATIENT_DOCTOR requires an appointment — see ConversationEligibilityService),
// the backend's 400 is shown as a plain-language message, not a crash.

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AppShell } from '../components/Layout';
import { chatMessagingAPI } from '../services/chatMessagingAPI';
import { useHumanChat } from '../hooks/useHumanChat';
import HumanChatWindow from '../components/chat/HumanChatWindow';

export default function StaffConversationPage({ conversationType }) {
  const { patientId } = useParams();
  const navigate = useNavigate();

  const [conversation, setConversation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!patientId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);

    chatMessagingAPI.createOrGet(conversationType, patientId)
      .then((c) => { if (!cancelled) setConversation(c); })
      .catch((e) => { if (!cancelled) setError(e.message || 'Could not open this conversation.'); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [patientId, conversationType]);

  const chat = useHumanChat(conversation?.id);

  return (
    <AppShell breadcrumbs={[{ label: 'Chat' }]}>
      <div className="chat-pane" style={{ maxWidth: 700 }}>
        {loading && (
          <div style={{ padding: 24, fontSize: 13, color: 'var(--text-muted)' }}>Opening conversation…</div>
        )}

        {!loading && error && (
          <div className="alert alert-error" style={{ display: 'block', lineHeight: 1.6 }}>
            {error}
            <div style={{ marginTop: 12 }}>
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => navigate(-1)}
              >
                ← Back
              </button>
            </div>
          </div>
        )}

        {!loading && !error && conversation && (
          <HumanChatWindow
            counterpartName={conversation.counterpartName}
            counterpartRole={conversation.counterpartRole}
            counterpartId={conversation.counterpartId}
            messages={chat.messages}
            loading={chat.loading}
            error={chat.error}
            sending={chat.sending}
            onSend={chat.sendMessage}
          />
        )}
      </div>
    </AppShell>
  );
}
